import clsx from 'clsx';
// import { highlight, languages } from 'prismjs';
// import 'prismjs/components/prism-json';
// import 'prismjs/themes/prism.css';
import React, {
  ReactElement, Suspense,useEffect, useState
} from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
// import Editor from 'react-simple-code-editor';
import { toast,ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

import APIGateway from '@/api';
import { Loader } from '@/components/loader/loader';
import {
  METRIC_TYPES, MetricCloneReq, MetricConfig, MetricConfigGetRes, MetricCreateReq,MetricGetRes, MetricProvider, MetricVersionCreateReq, MetricVersionDeprecateReq
} from '@/types/evals';
import { parseParams } from '@/utils/searchParams';

import ModalMetricCreate from '../_modalMetricCreate/modalMetricCreate';
import styles from './page.module.scss';

type SearchParams = {
  metricProviderId: string;
  version: string;
};

function EvaluationMetricPage() {
  /* DATA */
  const [ searchParams, setSearchParams ] = useSearchParams();
  const {
    metricProviderId: qMetricProviderId = '',
    version: qVersion = ''
  } = parseParams<SearchParams>( searchParams );

  const [ isLoading, setIsLoading ] = useState<boolean>( true );
  const [ metricProvider, setMetricProvider ] = useState<MetricProvider>();
  const [ metricConfig, setMetricConfig ] = useState<MetricConfig>();
  const [ _isDeprecating, setIsDeprecating ] = useState<boolean>( false );
  const [ showModalCreate, setShowModalCreate ] = useState<boolean>( false );
  const [ modalMode, setModalMode ] = useState<'create' | 'clone' | 'version'>( 'create' );
  const [ isCreatingNewVersion, setIsCreatingNewVersion ] = useState<boolean>( false );
  const [ isCloningMetric, setIsCloningMetric ] = useState<boolean>( false );
  const navigate = useNavigate();


  /* HOOKS */
  useEffect( () => {
    _fetchData();
  }, [ qMetricProviderId, qVersion ] );



  /* HELPERS */
  const _fetchData = async() => {
    setIsLoading( true );
    try {
      const metric: MetricGetRes = await APIGateway.evals.metricGet( qMetricProviderId );
      setMetricProvider( metric );

      const config: MetricConfigGetRes = await APIGateway.evals.metricConfigGet( qMetricProviderId, Number( qVersion ) );
      setMetricConfig( config );
    } catch( e ) {
      console.error( e );
      const errorMessage = e instanceof Error ? e.message : 'Unable to get metric config';
      toast( errorMessage );
    } finally {
      setIsLoading( false );
    }
  };

  const _buildDoms = ( obj?: object, parentKey = '' ): ReactElement[] => {
    if( !obj ) return [ <></> ];

    return Object.entries( obj ).map( ( [ key, value ] ) => {
      const fullKey = parentKey ? `${ parentKey }.${ key }` : key;

      if ( typeof value === 'object' && value !== null ) {
        return _buildDoms( value, fullKey );
      }

      return (
        <div className={ clsx( 'layout__col' ) } key={ `metric-key-${ fullKey }` }>
          <fieldset>
            <legend className={ styles.key }>{ fullKey }</legend>
            <p className={ styles.val }>{ value !== undefined && value !== null ? value.toString() : '-' }</p>
          </fieldset>
        </div>
      );
    } ).flat();
  };

  const _cloneMetric = async( args: MetricCreateReq ) => {
    if( !metricConfig || !metricProvider ) return;

    setIsCloningMetric( true );
    try {
      // Get prompt parameters directly from the configuration
      const promptParams = ( 'promptParameters' in args.configuration ) ? args.configuration.promptParameters : undefined;

      const requestBody: MetricCloneReq = {
        name: args.name,
        description: args.description,
        ...( promptParams && { promptParameters: promptParams } )
      };
      const resp = await APIGateway.evals.metricClone( qMetricProviderId, qVersion, requestBody );

      setMetricProvider( resp );

      toast( `Metric cloned successfully (${ resp.name })` );
      setShowModalCreate( false );
      // navigate to the new cloned metric page
      navigate( `/evaluations/metric?metricProviderId=${ resp.id }&version=1` );
    } catch( e ) {
      console.error( e );
      const errorMessage = e instanceof Error ? e.message : 'Unable to clone metric';
      toast( errorMessage );
    } finally {
      setIsCloningMetric( false );
    }
  };

  const _setDeprecation = async( isDeprecated: boolean ) => {
    if( !metricConfig ) return;
    setIsDeprecating( true );

    try {
      const depReq: MetricVersionDeprecateReq = { isDeprecated: isDeprecated };
      const config = await APIGateway.evals.metricVersionDeprecate( qMetricProviderId, Number( qVersion ), depReq );
      setMetricConfig( config );

      toast( `metric ${ isDeprecated ? 'deprecated' : 'undeprecated' }` );
    } catch( e ) {
      console.error( e );
      const errorMessage = e instanceof Error ? e.message : 'Unable to update deprecation status';
      toast( errorMessage );
    } finally {
      setIsDeprecating( false );
    }
  };

  const _onChangeDisplayedVersion = ( version: number ) => {
    const params = new URLSearchParams( searchParams.toString() );
    params.set( 'version', version.toString() );
    setSearchParams( params );
  };

  const _onCreateNewVersion = async( args: MetricCreateReq ) => {
    if( !metricConfig ) return;

    setIsCreatingNewVersion( true );
    try {
      const requestBody: MetricVersionCreateReq = {
        exampleType: metricConfig?.exampleType ?? [],
        configuration: args.configuration,
        producesMetrics: args.producesMetrics
      };
      const config = await APIGateway.evals.metricVersionCreate( qMetricProviderId, requestBody );

      setMetricProvider(
        prev => ( prev
          ? {
              ...prev,
              versions: [ ...prev.versions, { id: config.versionId, version: config.version ?? 0 } ]
            }
          : undefined )
      );

      toast( `New metric version (${ config.version }) created` );
      setShowModalCreate( false );
      _onChangeDisplayedVersion( config.version ?? 1 );
    } catch( e ) {
      console.error( e );
      const errorMessage = e instanceof Error ? e.message : 'Unable to create new metric version';
      toast( errorMessage );
    } finally {
      setIsCreatingNewVersion( false );
    }
  };


  /* RENDERS */
  const renderInfo = (): ReactElement => (
    <>
      <span className={ clsx( styles.info_page ) }>
        <p>Metric Configuration</p>
      </span>
      <span className={ styles.info_name }>
        <p>{ metricConfig?.name }</p>
      </span>
      <div className={ clsx( 'layout__row', styles.deprecate ) }>
        <span>Deprecated</span>
        <input
          checked={ metricConfig?.isDeprecated }
          onChange={ e => _setDeprecation( e.target.checked ) }
          type='checkbox'
          data-testid='deprecate-checkbox'
        />
      </div>
      <div className={ clsx( 'layout__row', styles.clone_button ) }>
        {
          <button
            className='clone_button'
            onClick={ () => {
              setModalMode( 'clone' );
              setShowModalCreate( true );
            } }
            disabled={ !metricConfig || isCloningMetric }
            data-testid='clone-button'
          >
            { isCloningMetric ? 'Cloning...' : 'Clone Metric' }
          </button>
        }
      </div>

      <div className={ clsx( 'layout__row', 'space_none', styles.info_version ) }>
        <p>Version</p>
        <select value={ Number( qVersion ) } onChange={ ( e ) => _onChangeDisplayedVersion( parseInt( e.target.value ) ) } data-testid='version-dropdown'>
          {
            metricProvider?.versions?.map( ( v ) => (
              <option value={ v.version } key={ `version-option-${ v.version }` }>{ v.version }</option>
            ) )
          }
        </select>
      </div>
      <div className={ styles.info_actions }>
        {
          <button
            className='create_button'
            onClick={ () => {
              setModalMode( 'version' );
              setShowModalCreate( prev => !prev );
            } }
            disabled={ !metricConfig || isCreatingNewVersion }
            data-testid='create-version-button'
          >
            { isCreatingNewVersion ? 'Creating...' : 'New Version' }
          </button>
        }
      </div>
    </>
  );

  const renderDescription = (): ReactElement => (
    <span className={ styles.info_description }>{ metricProvider?.description }</span>
  );

  const renderMetricConfig = (): ReactElement => {
    const { configuration, producesMetrics, ...general } = metricConfig || {};
    const config = { ...configuration, producesMetrics };

    return (
      <>
        <div className={ clsx( 'layout__col', 'pad', styles.metric_section ) }>
          <div className={ clsx( 'layout__row', 'pad' ) }>
            <h4>General Properties</h4>
          </div>
          <div className={ clsx( 'layout__col', styles.metric_pane ) }>
            {
              _buildDoms( general )
            }
          </div>
        </div>
        <div className={ clsx( 'layout__col', 'pad', styles.metric_section ) }>
          <div className={ clsx( 'layout__row', 'pad' ) }>
            <h4>Config Properties</h4>
          </div>
          <div className={ clsx( 'layout__col', styles.metric_pane ) }>
            {
              _buildDoms( config )
            }
          </div>
        </div>
      </>
    );
  };

  const metricDataToEdit = metricConfig
    ? { ...( 'url' in metricConfig.configuration
        ? {
            type: METRIC_TYPES.API_CLASSIFIER,
            config: metricConfig.configuration
          }
        : {
            type: METRIC_TYPES.LLM_AS_A_JUDGE,
            config: metricConfig.configuration
          } ),
      producesMetrics: metricConfig?.producesMetrics,
      exampleType: metricConfig.exampleType,
      name: metricProvider?.name,
      description: metricProvider?.description }
    : undefined;

  const render = (): ReactElement => (
    <>
      {
        metricConfig && showModalCreate && (
          <ModalMetricCreate
            mode={ modalMode }
            cb={ modalMode === 'clone' ? _cloneMetric : _onCreateNewVersion }
            onClose={ () => setShowModalCreate( false ) }
            initial={ metricDataToEdit }
            isSubmitting={ modalMode === 'clone' ? isCloningMetric : isCreatingNewVersion }
          />
        )
      }
      <div className={ clsx( 'layout__row', styles.layout__info ) }>
        { renderInfo() }
      </div>
      <div className={ clsx( 'layout__row', 'pad', styles.layout__description ) }>
        { renderDescription() }
      </div>
      <div className={ clsx( 'layout__row', 'pad', styles.layout__metric ) }>
        { renderMetricConfig() }
      </div>
    </>
  );

  return (
    <div className='page'>
      <ToastContainer />
      {
        isLoading
          ? <Loader />
          : render()
      }
    </div>
  );
}

export default function Page() {
  return (
    <Suspense fallback={ <Loader /> }>
      <EvaluationMetricPage />
    </Suspense>
  );
}
